import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { assemblyService } from '../services/assemblyService';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';

const isAssemblyTraceEnabled = (): boolean => localStorage.getItem('traceRealtime') === '1';

const traceAssembly = (...args: unknown[]): void => {
  if (!isAssemblyTraceEnabled()) return;
  console.log(...args);
};

interface AssemblyContextType {
  currentAssemblyId: string | null;
  joinAssembly: (assemblyId: string) => Promise<void>;
  leaveCurrentAssembly: () => Promise<void>;
  isInAssembly: boolean;
}

const AssemblyContext = createContext<AssemblyContextType | undefined>(undefined);

export const AssemblyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ✅ Restaurar asamblea seleccionada del admin desde localStorage si existe
  const [currentAssemblyId, setCurrentAssemblyId] = useState<string | null>(() => {
    const saved = localStorage.getItem('adminSelectedAssembly');
    return saved || null;
  });
  const { user, isLoading } = useAuth();
  const socketContext = useSocket();
  const { isConnected } = socketContext;

  // Logging de alto volumen solo cuando traceRealtime=1
  traceAssembly('[AssemblyContext] Render - currentAssemblyId:', currentAssemblyId, 'lastAssemblyCompletedId:', socketContext.lastAssemblyCompletedId);

  // Cargar asamblea activa automáticamente cuando el usuario inicia sesión
  useEffect(() => {
    const loadActiveAssembly = async () => {
      // Esperar a que cargue el usuario para no borrar la selección al recargar
      if (isLoading) return;

      // ⚠️ Esperar a que el socket esté conectado antes de intentar unirse
      if (!isConnected) {
        traceAssembly('⏳ [AssemblyContext] Esperando conexión socket antes de cargar asamblea...');
        return;
      }

      if (!user) {
        setCurrentAssemblyId(null);
        localStorage.removeItem('adminSelectedAssembly');
        return;
      }

      try {
        // ✅ Primero intentar restaurar asamblea guardada (para admin)
        const savedAssemblyId = localStorage.getItem('adminSelectedAssembly');
        if (savedAssemblyId && user.role === 'admin') {
          try {
            const assembly = await assemblyService.getAssemblyOverviewView(savedAssemblyId);
            if (assembly) {
              traceAssembly('✅ Asamblea guardada restaurada:', assembly.id);
              await joinAssembly(assembly.id);
              return;
            }
          } catch (error) {
            console.warn('⚠️ No se pudo restaurar asamblea guardada, usando activa', error);
            localStorage.removeItem('adminSelectedAssembly');
          }
        }

        // Si no hay guardada o no es admin, buscar asamblea activa
        const activeAssembly = await assemblyService.getMyActiveAssembly();
        
        if (activeAssembly && activeAssembly.id !== currentAssemblyId) {
          traceAssembly('✅ Asamblea activa encontrada:', activeAssembly.id);
          // Unirse automáticamente
          await joinAssembly(activeAssembly.id);
        } else if (!activeAssembly && currentAssemblyId) {
          // Ya no hay asamblea activa, salir
          traceAssembly('⚠️ No hay asamblea activa, saliendo de la asamblea actual');
          await leaveCurrentAssembly();
        }
      } catch (error) {
        console.error('Error cargando asamblea activa:', error);
      }
    };

    loadActiveAssembly();
  }, [user, isLoading, isConnected]); // ✅ Agregar isConnected como dependencia

  const joinAssembly = async (assemblyId: string) => {
    try {
      const targetAssembly = await assemblyService.getAssemblyOverviewView(assemblyId);
      const shouldJoinSocket = targetAssembly.status !== 'completed' && targetAssembly.status !== 'cancelled';

      // Evitar unirse múltiples veces a la misma asamblea
      if (currentAssemblyId === assemblyId) {
        traceAssembly('⚠️ Ya estás en esta asamblea:', assemblyId);
        return;
      }
      
      // Si ya está en otra asamblea, salir primero
      if (currentAssemblyId && currentAssemblyId !== assemblyId) {
        await socketContext.leaveAssembly(currentAssemblyId);
      }
      
      // Registrar asistencia en backend
      await assemblyService.joinAssembly(assemblyId);

      // Solo usar Socket.IO para asambleas con interacción en vivo
      if (shouldJoinSocket) {
        await socketContext.joinAssembly(assemblyId);
      } else {
        traceAssembly(`ℹ️ [AssemblyContext] Asamblea ${assemblyId} en estado ${targetAssembly.status}: omitiendo join de socket`);
      }

      setCurrentAssemblyId(assemblyId);
      
      traceAssembly('✅ Usuario unido a asamblea:', assemblyId);
      
      // Guardar en localStorage para persistencia
      localStorage.setItem('currentAssemblyId', assemblyId);
      // ✅ Para admin, guardar en adminSelectedAssembly para restaurar en F5
      if (user?.role === 'admin') {
        localStorage.setItem('adminSelectedAssembly', assemblyId);
        traceAssembly('💾 Asamblea guardada para admin:', assemblyId);
      }
    } catch (error) {
      console.error('Error joining assembly:', error);
      throw error;
    }
  };

  const leaveCurrentAssembly = useCallback(async () => {
    if (currentAssemblyId) {
      try {
        traceAssembly('📤 [AssemblyContext] leaveCurrentAssembly LLAMADA para:', currentAssemblyId);
        await socketContext.leaveAssembly(currentAssemblyId);
        setCurrentAssemblyId(null);
        
        // Limpiar también la asamblea seleccionada del App.tsx
        localStorage.removeItem('currentAssemblyId');
        localStorage.removeItem('selectedAssembly');
        // ✅ Limpiar adminSelectedAssembly si es admin
        localStorage.removeItem('adminSelectedAssembly');
        
        traceAssembly('✅ Usuario salió de asamblea via HTTP y Socket.IO');
      } catch (error) {
        console.error('Error leaving assembly:', error);
        // Incluso si hay error, limpiamos el estado local
        setCurrentAssemblyId(null);
        localStorage.removeItem('currentAssemblyId');
        localStorage.removeItem('selectedAssembly');
        localStorage.removeItem('adminSelectedAssembly');
      }
    }
  }, [currentAssemblyId, socketContext]);

  // NO restaurar asamblea del localStorage aquí - lo hace App.tsx
  // para evitar ciclos infinitos
  // useEffect(() => {
  //   const savedAssemblyId = localStorage.getItem('currentAssemblyId');
  //   if (savedAssemblyId && user) {
  //     setCurrentAssemblyId(savedAssemblyId);
  //   }
  // }, [user]);

  // Salir de asamblea cuando el usuario hace logout
  // DESACTIVADO: Esto causaba duplicación de llamadas durante logout manual
  // useEffect(() => {
  //   if (!user && currentAssemblyId) {
  //     console.log('🔌 [AssemblyContext] Usuario desconectado, saliendo de asamblea:', currentAssemblyId);
  //     leaveCurrentAssembly();
  //   }
  // }, [user, currentAssemblyId, leaveCurrentAssembly]);

  // Escuchar cuando el admin inicia una asamblea para unirse automáticamente
  useEffect(() => {
    const { lastAssemblyStartedId: lastStartedId } = socketContext;
    
    traceAssembly('🔍 [AssemblyContext] useEffect started disparado');
    traceAssembly('   - lastAssemblyStartedId:', lastStartedId);
    traceAssembly('   - user.role:', user?.role);
    traceAssembly('   - currentAssemblyId:', currentAssemblyId);
    
    if (!lastStartedId) {
      traceAssembly('⏭️ [AssemblyContext] lastStartedId es null/undefined, saltando');
      return;
    }
    
    if (user?.role === 'admin') {
      traceAssembly('⏭️ [AssemblyContext] Usuario es admin, saltando auto-join');
      return;
    }
    
    if (currentAssemblyId) {
      traceAssembly('⏭️ [AssemblyContext] Ya estás en una asamblea, saltando');
      return;
    }
    
    traceAssembly('📥 [AssemblyContext] ✅ Auto-join EJECUTÁNDOSE para asamblea:', lastStartedId);
    joinAssembly(lastStartedId)
      .then(() => {
        // 🧹 Limpiar evento después de procesar
        socketContext.clearAssemblyStartedEvent();
      })
      .catch(err => {
        console.error('❌ Error al unirse automáticamente a asamblea:', err);
      });
  }, [socketContext.lastAssemblyStartedId, user, currentAssemblyId, joinAssembly, socketContext]);

  // Escuchar cuando la asamblea se completa para salir automáticamente
  useEffect(() => {
    const { lastAssemblyCompletedId: lastCompletedId } = socketContext;
    
    traceAssembly('🔍 [AssemblyContext] useEffect completed disparado');
    traceAssembly('   - lastAssemblyCompletedId:', lastCompletedId);
    traceAssembly('   - currentAssemblyId:', currentAssemblyId);
    
    if (!lastCompletedId) {
      traceAssembly('⏭️ [AssemblyContext] lastCompletedId es null/undefined, saltando');
      return;
    }
    
    if (currentAssemblyId !== lastCompletedId) {
      traceAssembly('⏭️ [AssemblyContext] IDs no coinciden:', currentAssemblyId, '!==', lastCompletedId);
      return;
    }
    
    traceAssembly('📤 [AssemblyContext] ✅ Auto-leave EJECUTÁNDOSE para asamblea:', lastCompletedId);
    leaveCurrentAssembly()
      .then(() => {
        // 🧹 Limpiar eventos después de procesar para evitar loops infinitos
        socketContext.clearAssemblyCompletedEvent();
        socketContext.clearAssemblyStartedEvent();
      })
      .catch(err => {
        console.error('❌ Error al salir automáticamente de asamblea:', err);
        // 🧹 Incluso si hay error, limpiar eventos
        socketContext.clearAssemblyCompletedEvent();
        socketContext.clearAssemblyStartedEvent();
      });
  }, [socketContext.lastAssemblyCompletedId, currentAssemblyId, leaveCurrentAssembly, socketContext]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      if (currentAssemblyId) {
        // Intentar salir de la asamblea al cerrar la aplicación
        assemblyService.leaveAssembly(currentAssemblyId).catch(console.error);
      }
    };
  }, [currentAssemblyId]);

  const value: AssemblyContextType = {
    currentAssemblyId,
    joinAssembly,
    leaveCurrentAssembly,
    isInAssembly: !!currentAssemblyId
  };

  return (
    <AssemblyContext.Provider value={value}>
      {children}
    </AssemblyContext.Provider>
  );
};

export const useAssembly = () => {
  const context = useContext(AssemblyContext);
  if (context === undefined) {
    throw new Error('useAssembly must be used within an AssemblyProvider');
  }
  return context;
};